/**
 * SmartListEditor Component
 * 
 * Modal for creating and editing Smart Lists.
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  Switch,
  Button,
  Text,
  Badge,
  Divider,
  useColorModeValue,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FiSave, FiFilter } from 'react-icons/fi';
import { FilterBuilder } from './Filters';
import { useCrmSettings } from '../../contexts/CrmSettingsContext';

/**
 * SmartListEditor - Modal for creating/editing Smart Lists
 */
export default function SmartListEditor({
  isOpen,
  onClose,
  list = null,
  onSave,
  entityType = 'contacts',
  currentFilters = [],
  currentSort = {},
  visibleColumns = [],
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [filters, setFilters] = useState([]);
  const [isGlobal, setIsGlobal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  
  const bgColor = useColorModeValue('white', 'gray.800');
  
  // Get admin-configured options for filters
  const { contactTypes, contactTags, dealTags, dealStages } = useCrmSettings();
  
  // Initialize form when modal opens or list changes
  useEffect(() => {
    if (isOpen) {
      if (list) {
        // Editing existing list
        setName(list.name || '');
        setDescription(list.description || '');
        setFilters(list.filters || []);
        setIsGlobal(list.isGlobal || false);
      } else {
        // Creating new list with current filters
        setName('');
        setDescription('');
        setFilters(currentFilters || []);
        setIsGlobal(false);
      }
      setError(null);
    }
  }, [isOpen, list, currentFilters]);
  
  // Handle save
  const handleSave = async () => {
    if (!name.trim()) {
      setError('Please enter a name for this Smart List');
      return;
    }
    
    setIsSaving(true);
    setError(null);
    
    try {
      await onSave({
        name: name.trim(),
        description: description.trim(),
        filters,
        sortBy: currentSort.field,
        sortDirection: currentSort.direction,
        visibleColumns,
        isGlobal,
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save Smart List');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Handle filter changes from FilterBuilder
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };
  
  const isEditing = !!list;
  const activeFilterCount = filters.filter(f => f.field && f.operator).length;
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent bg={bgColor}>
        <ModalHeader>
          {isEditing ? 'Edit Smart List' : 'Create Smart List'}
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {error}
              </Alert>
            )}
            
            {/* Name */}
            <FormControl isRequired>
              <FormLabel>Name</FormLabel>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., High Value Leads, Active Borrowers"
              />
            </FormControl>
            
            {/* Description */}
            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description of what this list shows..."
                rows={2}
              />
            </FormControl>
            
            <Divider />
            
            {/* Filters */}
            <FormControl>
              <FormLabel>
                <HStack>
                  <FiFilter />
                  <Text>Filter Conditions</Text>
                  {activeFilterCount > 0 && (
                    <Badge colorScheme="blue">{activeFilterCount}</Badge>
                  )}
                </HStack>
              </FormLabel>
              <FormHelperText mb={2}>
                Define the conditions that determine which {entityType} appear in this list.
              </FormHelperText>
              <FilterBuilder
                filters={filters}
                onFiltersChange={handleFiltersChange}
                entityType={entityType}
                contactTypes={contactTypes}
                contactTags={contactTags}
                dealTags={dealTags}
                dealStages={dealStages}
              />
            </FormControl>
            
            <Divider />
            
            {/* Global/Shared toggle */}
            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <VStack align="flex-start" spacing={0}>
                <FormLabel mb={0}>Share with team</FormLabel>
                <FormHelperText mt={0}>
                  Make this Smart List available to all team members
                </FormHelperText>
              </VStack>
              <Switch
                isChecked={isGlobal}
                onChange={(e) => setIsGlobal(e.target.checked)}
                colorScheme="blue"
              />
            </FormControl>
            
            {/* Info about saved state */}
            <Alert status="info" borderRadius="md" fontSize="sm">
              <AlertIcon />
              <VStack align="flex-start" spacing={0}>
                <Text>This Smart List will save:</Text>
                <Text color="gray.600">
                  {activeFilterCount} filter condition{activeFilterCount !== 1 ? 's' : ''}
                  {currentSort.field && `, sorted by ${currentSort.field}`}
                </Text>
              </VStack>
            </Alert>
          </VStack>
        </ModalBody>
        
        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              leftIcon={<FiSave />}
              onClick={handleSave}
              isLoading={isSaving}
              loadingText="Saving..."
            >
              {isEditing ? 'Save Changes' : 'Create Smart List'}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
